import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Radio, Sparkles, HardDrive, Wand2, Save, Loader2 } from "lucide-react";

const STATUS_BG = { APPROVED: "bg-[#86EFAC]", PENDING_ADMIN_APPROVAL: "bg-[#FDE047]", REJECTED: "bg-[#FCA5A5]" };

function QuotaBar({ quota }) {
  if (!quota) return null;
  const limit = quota.storage_limit_bytes + (quota.extra_storage_gb || 0) * 1073741824;
  const pct = limit ? Math.min(100, Math.round((quota.storage_used_bytes / limit) * 100)) : 0;
  return (
    <div className="nb-card p-4 bg-[#C4B5FD] flex items-center gap-4" data-testid="creator-quota">
      <HardDrive size={22} />
      <div className="flex-1">
        <div className="text-sm font-bold">{quota.tier_name} Tier · {quota.allowed_boards} board(s) · Uploads {quota.allow_direct_uploads ? "ON" : "OFF"}</div>
        <div className="h-2 mt-1 border-2 border-black bg-white"><div className="h-full bg-[#86EFAC]" style={{ width: `${pct}%` }} /></div>
        <div className="text-xs mono mt-1">{(quota.storage_used_bytes / 1073741824).toFixed(2)} / {(limit / 1073741824).toFixed(0)} GB</div>
      </div>
    </div>
  );
}

function Content({ quota }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { title: "", description: "", content_type: "VIDEO", source: "EXTERNAL", external_url: "", board: "", grade: "", subject: "", chapter_name: "", lesson_number: "", thumbnail_url: "" };
  const [f, setF] = useState(blank);
  const load = () => api.get("/creators/content").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    if (f.source === "EXTERNAL" && !f.external_url) { toast.error("External URL required"); return; }
    try {
      const r = await api.post("/creators/content", f);
      toast.success(r.data.status === "APPROVED" ? "Published (auto-approved)" : "Submitted for review");
      setOpen(false); setF(blank); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <QuotaBar quota={quota} />
      <div className="flex justify-between items-center"><h2 className="display text-2xl font-bold">My Content</h2>
        <button className="nb-btn" onClick={() => setOpen(true)} data-testid="creator-add-content"><Plus size={16} /> New Content</button></div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Title</th><th>Curriculum</th><th>Source</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.content_id} data-testid={`creator-content-${c.content_id}`}>
                <td className="font-bold">{c.title}</td>
                <td className="text-xs">{c.board} {c.grade} · {c.subject}</td>
                <td className="text-xs mono">{c.source}</td>
                <td><span className={`nb-badge ${STATUS_BG[c.status] || "bg-white"}`}>{c.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No content yet</td></tr>}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="nb-card bg-white p-5 max-w-lg w-full space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="display text-lg font-bold">New Content</h3>
            <input className="nb-input" placeholder="Title *" value={f.title} onChange={set("title")} data-testid="content-title" />
            <textarea className="nb-input" rows="2" placeholder="Description" value={f.description} onChange={set("description")} data-testid="content-desc" />
            <div className="grid grid-cols-2 gap-2">
              <select className="nb-select" value={f.source} onChange={set("source")} data-testid="content-source">
                <option value="EXTERNAL">External Link (YouTube/Bunny)</option>
                <option value="UPLOAD" disabled={!quota?.allow_direct_uploads}>Direct Upload {quota?.allow_direct_uploads ? "" : "(tier locked)"}</option>
              </select>
              <select className="nb-select" value={f.content_type} onChange={set("content_type")}>
                <option value="VIDEO">Video</option><option value="NOTE">Note</option><option value="COURSE">Course</option>
              </select>
            </div>
            {f.source === "EXTERNAL" && <input className="nb-input" placeholder="YouTube / Bunny URL *" value={f.external_url} onChange={set("external_url")} data-testid="content-url" />}
            <div className="grid grid-cols-3 gap-2">
              <input className="nb-input" placeholder="Board" value={f.board} onChange={set("board")} data-testid="content-board" />
              <input className="nb-input" placeholder="Grade" value={f.grade} onChange={set("grade")} data-testid="content-grade" />
              <input className="nb-input" placeholder="Subject" value={f.subject} onChange={set("subject")} data-testid="content-subject" />
              <input className="nb-input" placeholder="Chapter" value={f.chapter_name} onChange={set("chapter_name")} />
              <input className="nb-input" placeholder="Lesson #" value={f.lesson_number} onChange={set("lesson_number")} />
              <input className="nb-input" placeholder="Thumbnail URL" value={f.thumbnail_url} onChange={set("thumbnail_url")} />
            </div>
            <div className="flex justify-end gap-2"><button className="nb-btn nb-btn-ghost text-xs" onClick={() => setOpen(false)}>Cancel</button>
              <button className="nb-btn text-xs" onClick={create} data-testid="content-save">Submit</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Live({ teamsOk }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", platform: "GOOGLE_MEET", meeting_url: "", board: "", grade: "", subject: "", scheduled_at: "" });
  const load = () => api.get("/creators/live-sessions").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    try { await api.post("/creators/live-sessions", f); toast.success("Session scheduled"); load(); setF({ ...f, title: "", meeting_url: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <h2 className="display text-2xl font-bold flex items-center gap-2"><Radio size={22} /> Live Sessions</h2>
      <div className="nb-card p-4 space-y-3 max-w-lg">
        <input className="nb-input" placeholder="Session title *" value={f.title} onChange={set("title")} data-testid="live-title" />
        <div className="grid grid-cols-2 gap-2">
          <select className="nb-select" value={f.platform} onChange={set("platform")} data-testid="live-platform">
            <option value="GOOGLE_MEET">Google Meet</option>
            <option value="MS_TEAMS" disabled={!teamsOk}>Microsoft Teams {teamsOk ? "" : "(configure creds)"}</option>
          </select>
          <input type="datetime-local" className="nb-input" value={f.scheduled_at} onChange={set("scheduled_at")} data-testid="live-when" />
        </div>
        {f.platform === "MS_TEAMS" && !teamsOk && <div className="text-xs text-amber-600">Configure Teams credentials in Global Admin settings to enable this.</div>}
        <input className="nb-input" placeholder="Meeting URL" value={f.meeting_url} onChange={set("meeting_url")} data-testid="live-url" />
        <div className="grid grid-cols-3 gap-2">
          <input className="nb-input" placeholder="Board" value={f.board} onChange={set("board")} />
          <input className="nb-input" placeholder="Grade" value={f.grade} onChange={set("grade")} />
          <input className="nb-input" placeholder="Subject" value={f.subject} onChange={set("subject")} />
        </div>
        <button className="nb-btn" onClick={create} data-testid="live-create">Schedule Session</button>
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table"><thead><tr><th>Title</th><th>Platform</th><th>When</th><th>Curriculum</th></tr></thead>
          <tbody>{rows.map(s => <tr key={s.session_id}><td className="font-bold">{s.title}</td><td className="mono text-xs">{s.platform}</td><td className="text-xs">{s.scheduled_at?.replace("T", " ")}</td><td className="text-xs">{s.board} {s.grade} · {s.subject}</td></tr>)}
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No sessions yet</td></tr>}</tbody></table>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [me, setMe] = useState(null);
  const [key, setKey] = useState("");
  const [desk, setDesk] = useState({ doubt_desk_address: "", doubt_desk_lat: "", doubt_desk_lng: "" });
  const [ai, setAi] = useState({ surface: "lesson_summary", topic: "" });
  const [aiOut, setAiOut] = useState(null);
  const loadMe = () => api.get("/creators/me").then(r => { setMe(r.data); setDesk({ doubt_desk_address: r.data.doubt_desk_address || "", doubt_desk_lat: r.data.doubt_desk_lat ?? "", doubt_desk_lng: r.data.doubt_desk_lng ?? "" }); });
  useEffect(() => { loadMe().catch(() => {}); }, []);
  const saveKey = async () => {
    try { await api.patch("/creators/me", { gemini_api_key: key }); toast.success("Gemini key saved"); setKey(""); loadMe(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveDesk = async () => {
    try {
      await api.patch("/creators/me", {
        doubt_desk_address: desk.doubt_desk_address,
        doubt_desk_lat: desk.doubt_desk_lat === "" ? null : parseFloat(desk.doubt_desk_lat),
        doubt_desk_lng: desk.doubt_desk_lng === "" ? null : parseFloat(desk.doubt_desk_lng),
      });
      toast.success("Doubt Desk saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const generate = async () => {
    try { const r = await api.post("/creators/ai/generate", ai); setAiOut(r.data.result); toast.success("Generated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!me) return <div className="mono">Loading…</div>;
  return (
    <div className="space-y-4 max-w-lg">
      <div className="nb-card p-6 space-y-2" data-testid="creator-profile">
        <h2 className="display text-xl font-bold">{me.display_name}</h2>
        <div className="text-sm">Tier: <b>{me.tier_key}</b> · Status: <span className={`nb-badge ${me.status === "ACTIVE" ? "bg-[#86EFAC]" : "bg-[#FDE047]"}`}>{me.status}</span></div>
        <div className="text-xs flex gap-2">
          <span className={`nb-badge ${me.has_course_creator_access !== false ? "bg-[#86EFAC]" : "bg-white"}`}>Course {me.has_course_creator_access !== false ? "ON" : "OFF"}</span>
          <span className={`nb-badge ${me.has_tv_channel_access !== false ? "bg-[#86EFAC]" : "bg-white"}`}>TV {me.has_tv_channel_access !== false ? "ON" : "OFF"}</span>
        </div>
        {me.is_hybrid_educator && <div className="text-xs">Hybrid Educator · Revenue split {me.revenue_split_educator_pct ?? 70}/{me.revenue_split_platform_pct ?? 30}</div>}
        {me.is_kids_approved && <div className="nb-badge bg-[#93C5FD] text-xs">✓ Kids-Approved (moderation bypass)</div>}
      </div>

      {me.has_course_creator_access !== false && (
        <div className="nb-card p-6 space-y-3" data-testid="creator-doubt-desk">
          <h3 className="display text-lg font-bold">Physical Doubt Desk</h3>
          <input className="nb-input" placeholder="Walk-in address" value={desk.doubt_desk_address} onChange={e => setDesk({ ...desk, doubt_desk_address: e.target.value })} data-testid="doubt-address" />
          <div className="grid grid-cols-2 gap-2">
            <input className="nb-input mono" placeholder="Latitude" value={desk.doubt_desk_lat} onChange={e => setDesk({ ...desk, doubt_desk_lat: e.target.value })} data-testid="doubt-lat" />
            <input className="nb-input mono" placeholder="Longitude" value={desk.doubt_desk_lng} onChange={e => setDesk({ ...desk, doubt_desk_lng: e.target.value })} data-testid="doubt-lng" />
          </div>
          <button className="nb-btn text-xs" onClick={saveDesk} data-testid="doubt-save">Save Doubt Desk</button>
        </div>
      )}

      <div className="nb-card p-6 space-y-3 bg-[#FDE047]">
        <h3 className="display text-lg font-bold flex items-center gap-2"><Sparkles size={18} /> BYOK — Your Gemini Key</h3>
        {!me.has_gemini_key && <div className="nb-card-sm p-2 bg-white text-xs border-2 border-black" data-testid="creator-byok-banner">Add your personal Gemini API key in Settings to unlock AI course tools.</div>}
        <div className="text-xs">Your own key powers the LMS course AI (quizzes, summaries). {me.has_gemini_key ? "✓ Key on file." : ""}</div>
        <div className="flex gap-2"><input type="password" className="nb-input flex-1" placeholder="Paste your Gemini API key" value={key} onChange={e => setKey(e.target.value)} data-testid="creator-gemini-key" />
          <button className="nb-btn text-xs" onClick={saveKey} data-testid="creator-gemini-save">Save</button></div>
        <div className="border-t-2 border-black pt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select className="nb-select text-xs" value={ai.surface} onChange={e => setAi({ ...ai, surface: e.target.value })}>
              <option value="lesson_summary">Lesson Summary</option><option value="chapter_quiz">Chapter Quiz</option><option value="syllabus">Syllabus</option>
            </select>
            <input className="nb-input text-xs" placeholder="Topic" value={ai.topic} onChange={e => setAi({ ...ai, topic: e.target.value })} data-testid="creator-ai-topic" />
          </div>
          <button className="nb-btn text-xs" onClick={generate} disabled={!me.has_gemini_key} data-testid="creator-ai-generate">Generate with my key</button>
          {aiOut && <pre className="text-[10px] bg-white border-2 border-black rounded p-2 overflow-auto max-h-40">{JSON.stringify(aiOut, null, 2)}</pre>}
        </div>
      </div>
    </div>
  );
}

function AICourseStudio({ me }) {
  const [tax, setTax] = useState({ boards: [], grades: [], subjects: [] });
  const [f, setF] = useState({ board: "", grade: "", subject: "", chapter: "", surface: "syllabus", notes: "" });
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const hasKey = me?.has_gemini_key;

  useEffect(() => {
    api.get("/taxonomy/boards").then(r => setTax(t => ({ ...t, boards: r.data }))).catch(() => {});
    api.get("/taxonomy/subjects").then(r => setTax(t => ({ ...t, subjects: r.data }))).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/taxonomy/grades", { params: { board_code: f.board || undefined } })
      .then(r => setTax(t => ({ ...t, grades: r.data && r.data.length ? r.data : ["G09","G10","G11","G12"] }))).catch(() => {});
  }, [f.board]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const generate = async () => {
    if (!f.board || !f.grade || !f.subject) { toast.error("Pick board, grade and subject"); return; }
    setBusy(true); setOut(null);
    const topic = `${f.board} · Grade ${f.grade} · ${f.subject}${f.chapter ? ` · Chapter: ${f.chapter}` : ""}${f.notes ? ` · Focus: ${f.notes}` : ""}`;
    try {
      const r = await api.post("/creators/ai/generate", { surface: f.surface, topic });
      setOut(r.data.result);
      toast.success("Generated with your Gemini key");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setBusy(false); }
  };
  const saveAsCourse = async () => {
    if (!out) return;
    setSaving(true);
    try {
      await api.post("/creators/content", {
        title: `${f.subject} — ${f.surface === "chapter_quiz" ? "Quiz" : f.surface === "lesson_summary" ? "Lesson" : "Syllabus"}${f.chapter ? `: ${f.chapter}` : ""}`,
        description: JSON.stringify(out).slice(0, 4000),
        content_type: f.surface === "chapter_quiz" ? "NOTE" : "COURSE",
        source: "EXTERNAL", external_url: "https://ponderr.school/ai-course",
        board: f.board, grade: f.grade, subject: f.subject, chapter_name: f.chapter,
      });
      toast.success("Saved to your content library (submitted for review)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><Wand2 size={22} /><h2 className="display text-2xl font-bold">AI Course Studio</h2></div>
      {!hasKey && (
        <div className="nb-card bg-[#FDE047] p-4 text-sm" data-testid="ai-studio-nokey">
          Add your personal <b>Gemini API key</b> in the <b>AI &amp; Settings</b> tab to unlock the course generator. This tool uses <b>only your own key (BYOK)</b>.
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="nb-card bg-white p-5 space-y-3">
          <h3 className="display text-lg font-bold flex items-center gap-2"><Sparkles size={18} /> Generate from live LMS taxonomy</h3>
          <div className="grid grid-cols-3 gap-2">
            <select className="nb-select text-sm" value={f.board} onChange={set("board")} data-testid="ai-board">
              <option value="">Board *</option>
              {tax.boards.map(b => <option key={b.code} value={b.code}>{b.code}</option>)}
            </select>
            <select className="nb-select text-sm" value={f.grade} onChange={set("grade")} data-testid="ai-grade">
              <option value="">Grade *</option>
              {tax.grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <select className="nb-select text-sm" value={f.subject} onChange={set("subject")} data-testid="ai-subject">
              <option value="">Subject *</option>
              {tax.subjects.map(s => <option key={s.subject_id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <input className="nb-input" placeholder="Chapter / unit (optional)" value={f.chapter} onChange={set("chapter")} data-testid="ai-chapter" />
          <select className="nb-select" value={f.surface} onChange={set("surface")} data-testid="ai-surface">
            <option value="syllabus">Full Syllabus / Course Outline</option>
            <option value="chapter_quiz">Chapter Quiz (MCQ)</option>
            <option value="lesson_summary">Lesson Summary</option>
          </select>
          <textarea className="nb-input" rows="2" placeholder="Extra focus or learning objectives (optional)" value={f.notes} onChange={set("notes")} data-testid="ai-notes" />
          <button className="nb-btn w-full justify-center" onClick={generate} disabled={busy || !hasKey} data-testid="ai-generate">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <><Wand2 size={16} /> Generate</>}
          </button>
        </div>
        <div className="nb-card bg-[#0F1020] text-white p-5 space-y-3 min-h-[220px]">
          <div className="flex items-center justify-between">
            <h3 className="display text-lg font-bold">Preview</h3>
            {out && <button className="nb-btn nb-btn-secondary text-xs" onClick={saveAsCourse} disabled={saving} data-testid="ai-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save as Course</>}</button>}
          </div>
          {out ? <pre className="text-[11px] bg-black/40 border border-white/20 rounded p-3 overflow-auto max-h-[420px] whitespace-pre-wrap" data-testid="ai-output">{JSON.stringify(out, null, 2)}</pre>
            : <div className="text-white/50 text-sm grid place-items-center h-full">Your AI-generated syllabus / quiz will appear here.</div>}
        </div>
      </div>
    </div>
  );
}

export default function CreatorDashboard() {
  const [me, setMe] = useState(null);
  const [quota, setQuota] = useState(null);
  const [teamsOk, setTeamsOk] = useState(false);
  useEffect(() => {
    api.get("/creators/me").then(r => setMe(r.data)).catch(() => {});
    api.get("/creators/me/quota").then(r => setQuota(r.data)).catch(() => {});
    api.get("/tv/integrations/status").then(r => setTeamsOk(r.data.teams_configured)).catch(() => {});
  }, []);

  const hasCourse = me ? me.has_course_creator_access !== false : true;
  const hasTv = me ? me.has_tv_channel_access !== false : true;

  const tabs = [];
  if (hasCourse) tabs.push({ id: "courses", label: "My Courses" });
  if (hasCourse) tabs.push({ id: "ai_studio", label: "AI Course Studio" });
  if (hasTv) tabs.push({ id: "tv", label: "TV Channel Manager" });
  tabs.push({ id: "live", label: hasTv && !hasCourse ? "Broadcast Schedule" : "Live Doubt Webinars" });
  tabs.push({ id: "settings", label: "AI & Settings" });

  const [tab, setTab] = useState("courses");
  useEffect(() => { if (me && !tabs.find(t => t.id === tab)) setTab(tabs[0]?.id || "settings"); }, [me]);

  return (
    <Shell title="Creator Studio" subtitle="Ponderr TV + LMS" tabs={tabs} active={tab} onTab={setTab}>
      {(tab === "courses" || tab === "tv") && <Content quota={quota} />}
      {tab === "ai_studio" && <AICourseStudio me={me} />}
      {tab === "live" && <Live teamsOk={teamsOk} />}
      {tab === "settings" && <SettingsPanel />}
    </Shell>
  );
}
